import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface Option {
    value: string
    label: string
}
interface InputConfig {
    id: string
    name: string
    placeholder: string
    type: string
    label?: string
    options?: Option[]
}

interface AdvancedSearchProps {
    setAdvancedSearch: (values: Record<string, any>) => void,
    inputs: InputConfig[],
    defaultState: Record<string, any>
    onClear?: () => void
}

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ setAdvancedSearch, inputs, defaultState, onClear }) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const advancedSearchKeys = useRef(
        new Set([
            ...Object.keys(defaultState),
            ...inputs.map(input => input.name),
        ])
    )

    const [localState, setLocalState] = useState(() => {
        const fromUrl: Record<string, any> = {}
        searchParams.forEach((value, key) => {
            if (advancedSearchKeys.current.has(key)) {
                fromUrl[key] = value
            }
        })
        return { ...defaultState, ...fromUrl }
    })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setLocalState(prevState => ({ ...prevState, [name]: value }))
    }
    type Timer = ReturnType<typeof setTimeout>
    const timeoutRef = useRef<Timer>()
    const clearFlag = useRef(false)

    useEffect(() => {
        if (clearFlag.current) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            setAdvancedSearch(localState)

            const params = new URLSearchParams(searchParams)

            for (const [key, value] of Object.entries(localState)) {
                if (!advancedSearchKeys.current.has(key)) {
                    continue
                }

                if (value !== '' && value !== null && value !== undefined) {
                    params.set(key, String(value))
                } else {
                    params.delete(key)
                }
            }

            if (params.toString() !== searchParams.toString()) {
                setSearchParams(params, { replace: true })
            }
        }, 500)
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [JSON.stringify(localState)])

    const handleClear = () => {
        clearFlag.current = true;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        setLocalState(defaultState)
        setAdvancedSearch(defaultState)

        if (onClear) {
            onClear()
        } else {
            const params = new URLSearchParams(searchParams)
            advancedSearchKeys.current.forEach(key => params.delete(key))
            setSearchParams(params, { replace: true })
        }
        setTimeout(() => { clearFlag.current = false; }, 0);
    }

    return (
        <div>
            <div className="row g-2 mb-3">
                {inputs.map((input) => (
                    <div className="col-md-3" key={input.id}>
                        {input.label && <label htmlFor={input.id}>{input.label}</label>}
                        {input.type !== 'dropdown' ? (
                            <input
                                type={input.type}
                                id={input.id}
                                name={input.name}
                                value={localState[input.name]}
                                placeholder={input.placeholder}
                                onChange={handleInputChange}
                                className="form-control"
                            />
                        ) : (
                            <select
                                id={input.id}
                                name={input.name}
                                value={localState[input.name]}
                                onChange={handleInputChange}
                                className="form-control"
                            >
                                <option value="">{input.placeholder}</option>
                                {input.options?.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                ))}
                <div className="col-md-2 d-flex align-items-end">
                    <button type="button" className="btn btn-secondary" onClick={handleClear}>
                        Clear
                    </button>
                </div>
            </div>
        </div>
    )
}
